<template>
  <div>
  <div class="main-container"
      :class="{ 'cart-margins': isCart }" 
      v-if="!(closedAndNotSupportingOffHourOrders(store)) && getStateOrderTypes.length && sortedOrderTypes && sortedOrderTypes.length" id="pickupDelivery">
    <div class="toggle-container">
      <div class="toggle-switch">
        <template v-for="item in sortedOrderTypes" :value="item.value">
          <button
            v-if="item.value !== 'DINE_IN'"
            :key="item.key"
            class="toggle-btn"
            :class="{ active: selectedOption === item.value }"
            @click="isGroupOrderGuest ? showGroupOrderLocked() : selectOption(item.value)"
          >
            {{ getOrderTypeTranslation(item.value) }}
          </button>
        </template>
      </div>
      <div v-if="!isCart && !isInGroupOrder && $store.getters.groupOrderEnabled" class="group-order-btn-wrap">
        <button class="group-order-btn" @click="onGroupOrderBtnClick">
          <v-icon small>group</v-icon>
          {{ $vuetify.t('$vuetify.groupOrder.title') }}
          <span v-if="showGroupOrderDot" class="go-pulse-dot"></span>
          <span
            v-if="hasSeenGroupOrderTooltip"
            class="go-howitworks-info"
            :aria-label="$vuetify.t('$vuetify.groupOrder.showHowItWorks')"
            @click.stop="onShowGroupOrderTooltipAgain"
          >?</span>
        </button>
        <GroupOrderOnboardingSheet
          v-if="showGroupOrderTooltip && isMobile"
          @start="onGroupOrderTooltipStart"
          @dismiss="onGroupOrderTooltipDismiss"
        />
        <GroupOrderOnboardingTooltip
          v-else-if="showGroupOrderTooltip"
          :store-name="getStore && getStore.name"
          @start="onGroupOrderTooltipStart"
          @dismiss="onGroupOrderTooltipDismiss"
        />
      </div>
    </div>

    <v-dialog v-model="showGroupOrderDialog" max-width="460" @input="onGroupOrderDialogInput">
      <v-card class="group-order-modal">

        <!-- Step 1: Main group order setup -->
        <template v-if="groupOrderStep === 'main'">
          <div class="go-modal-header">
            <span class="go-modal-title">{{ $vuetify.t('$vuetify.groupOrder.title') }}</span>
            <button class="go-modal-close" @click="showGroupOrderDialog = false">
              <v-icon>close</v-icon>
            </button>
          </div>

          <div class="go-modal-body">
            <input
              class="go-name-input"
              v-model="groupOrderName"
              :placeholder="$vuetify.t('$vuetify.groupOrder.namePlaceholder')"
              maxlength="30"
            />

            <div v-if="!isPickUp" class="go-info-row">
              <span class="go-info-label">{{ $vuetify.t('$vuetify.groupOrder.from') }}</span>
              <span class="go-info-value">{{ getStore && getStore.name }}</span>
            </div>
            <div class="go-info-row">
              <span class="go-info-label">{{ isPickUp ? $vuetify.t('$vuetify.groupOrder.pickupAt') : $vuetify.t('$vuetify.groupOrder.deliverTo') }}</span>
              <span class="go-info-value">{{ deliverToText }}</span>
            </div>

            <!-- Deadline setting row -->
            <button class="go-setting-row" :class="{ 'go-setting-row--disabled': !hasDeadlineOptions }" :disabled="!hasDeadlineOptions" @click="openDeadlineStep">
              <v-icon class="go-setting-icon" :color="hasDeadlineOptions ? '#111' : '#bbb'" size="26">schedule</v-icon>
              <div class="go-setting-left">
                <div class="go-setting-title" :style="!hasDeadlineOptions ? 'color: #bbb' : ''">{{ deadlineDisplayText }}</div>
                <div class="go-setting-sub">{{ deadlineDisplaySub }}</div>
              </div>
              <v-icon v-if="hasDeadlineOptions" class="go-setting-row-arrow" color="#888" size="18" style="flex-shrink: 0;">chevron_right</v-icon>
            </button>

            <!-- Payment setting row -->
            <button class="go-setting-row" @click="groupOrderStep = 'payment'">
              <v-icon class="go-setting-icon" color="#111" size="26">credit_card</v-icon>
              <div class="go-setting-left">
                <div class="go-setting-title">{{ paymentDisplayText }}</div>
                <div class="go-setting-sub">{{ paymentDisplaySub }}</div>
              </div>
              <v-icon class="go-setting-row-arrow" color="#888" size="18" style="transition: color 0.15s, transform 0.15s; flex-shrink: 0;">chevron_right</v-icon>
            </button>

            <button class="go-invite-btn" :disabled="inviteLoading" @click="startInviteFlow">
              <span v-if="inviteLoading"><v-progress-circular indeterminate size="16" width="2" color="white" style="margin-right:6px" /></span>
              {{ $vuetify.t('$vuetify.groupOrder.invitePeople') }}
            </button>
          </div>
        </template>

        <!-- Step: Set a Deadline -->
        <template v-else-if="groupOrderStep === 'deadline'">
          <div class="go-modal-header">
            <button class="go-modal-back" @click="deadlineOpenedFromCart ? showGroupOrderDialog = false : groupOrderStep = 'main'">
              <v-icon>arrow_back</v-icon>
            </button>
            <span class="go-modal-title">{{ $vuetify.t('$vuetify.groupOrder.deadlineTitle') }}</span>
            <button class="go-modal-close" @click="showGroupOrderDialog = false">
              <v-icon>close</v-icon>
            </button>
          </div>

          <div class="go-modal-body">
            <p class="go-pay-subtitle">{{ $vuetify.t('$vuetify.groupOrder.deadlineSubtitle') }}</p>

            <!-- Quick offset chips (only for scheduled orders, not ASAP) -->
            <div class="go-deadline-chips">
              <template v-if="!deadlineIsNow">
                <button
                  v-for="opt in deadlineOffsetOptions"
                  :key="opt.value"
                  class="go-deadline-chip"
                  :class="{ 'go-deadline-chip--selected': deadlineOffsetSelected === opt.value && !deadlineUseSpecific }"
                  @click="selectDeadlineOffset(opt.value)"
                >{{ opt.label }}</button>
              </template>
              <button
                v-if="hasSpecificTimeOptions"
                class="go-deadline-chip"
                :class="{ 'go-deadline-chip--selected': deadlineUseSpecific || deadlineIsNow }"
                @click="deadlineUseSpecific = true; deadlineOffsetSelected = null"
              >{{ $vuetify.t('$vuetify.groupOrder.deadlineSpecificTime') }}</button>
            </div>

            <!-- Specific time pickers (shown when "Specific time" chip selected and slots exist) -->
            <template v-if="deadlineUseSpecific && hasSpecificTimeOptions">
              <v-select
                v-model="deadlineDate"
                :items="deadlineDateOptions"
                item-value="id"
                item-text="label"
                :label="$vuetify.t('$vuetify.groupOrder.deadlineDayLabel')"
                dense
                hide-details
                @change="deadlineTime = null"
              />
              <v-select
                v-model="deadlineTime"
                :items="deadlineTimeOptions"
                item-value="id"
                item-text="label"
                :label="$vuetify.t('$vuetify.groupOrder.deadlineTimeLabel')"
                dense
                hide-details
                :disabled="!deadlineDate"
              />
            </template>

            <button class="go-invite-btn" :disabled="!canSaveDeadline" @click="saveDeadline">{{ $vuetify.t('$vuetify.common.save') }}</button>
            <button class="go-no-deadline-btn" @click="clearDeadline">{{ $vuetify.t('$vuetify.groupOrder.noDeadline') }}</button>
          </div>
        </template>

        <!-- Step: Payment / spending limit -->
        <template v-else-if="groupOrderStep === 'payment'">
          <div class="go-modal-header">
            <button class="go-modal-back" @click="groupOrderStep = 'main'">
              <v-icon>arrow_back</v-icon>
            </button>
            <span class="go-modal-title">{{ $vuetify.t('$vuetify.groupOrder.chooseWhoPays') }}</span>
            <button class="go-modal-close" @click="showGroupOrderDialog = false">
              <v-icon>close</v-icon>
            </button>
          </div>

          <div class="go-modal-body">
            <p class="go-pay-subtitle">{{ $vuetify.t('$vuetify.groupOrder.paySubtitle') }}</p>

            <div class="go-pay-option go-pay-option--selected">
              <div class="go-pay-option-row">
                <div class="go-pay-option-title">{{ $vuetify.t('$vuetify.groupOrder.youPayForEveryone') }}</div>
                <div class="go-pay-radio go-pay-radio--selected">
                  <div class="go-pay-radio-dot" />
                </div>
              </div>
            </div>

            <div class="go-limit-chips">
              <button
                v-for="chip in limitChips"
                :key="chip.value"
                class="go-limit-chip"
                :class="{ 'go-limit-chip--active': limitChip === chip.value }"
                @click="selectLimitChip(chip.value)"
              >{{ chip.label }}</button>
            </div>
            <div v-if="limitChip === 'other'" class="go-limit-input-wrap">
              <span class="go-limit-currency">{{ storeCurrencySymbol }}</span>
              <input
                class="go-limit-input"
                type="number"
                min="0"
                placeholder="0.00"
                v-model.number="groupOrderLimit"
                ref="limitInput"
                step="0.01"
                @keydown="if (['-', '+', 'e', 'E'].includes($event.key)) $event.preventDefault()"
                @input="if (groupOrderLimit !== '' && groupOrderLimit !== null) groupOrderLimit = Math.round(groupOrderLimit * 100) / 100"
              />
              <span class="go-limit-per-person">{{ $vuetify.t('$vuetify.groupOrder.perPerson') }}</span>
            </div>

            <div class="go-pay-option go-pay-option--disabled">
              <div class="go-pay-option-row">
                <div>
                  <div class="go-pay-option-title-row">
                    <span class="go-pay-option-title go-pay-option-title--muted">{{ $vuetify.t('$vuetify.groupOrder.everyonePaysThemselves') }}</span>
                    <span class="go-coming-soon-badge">{{ $vuetify.t('$vuetify.groupOrder.comingSoon') }}</span>
                  </div>
                </div>
                <div class="go-pay-radio">
                  <div class="go-pay-radio-dot go-pay-radio-dot--empty" />
                </div>
              </div>
            </div>

            <button class="go-invite-btn" @click="groupOrderStep = 'main'">{{ $vuetify.t('$vuetify.common.save') }}</button>
          </div>
        </template>

        <!-- Step: Invite People / QR -->
        <template v-else-if="groupOrderStep === 'invite'">
          <div class="go-modal-header">
            <button v-if="!inviteOpenedFromCart" class="go-modal-back" @click="groupOrderStep = 'main'">
              <v-icon>arrow_back</v-icon>
            </button>
            <span class="go-modal-title">{{ $vuetify.t('$vuetify.groupOrder.invitePeople') }}</span>
            <button class="go-modal-close" @click="showGroupOrderDialog = false">
              <v-icon>close</v-icon>
            </button>
          </div>

          <div class="go-modal-body">
            <template v-if="inviteLoading">
              <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;padding:40px 0">
                <v-progress-circular indeterminate size="40" width="3" color="#111" />
                <p style="margin-top:16px;font-size:14px;color:#888">Creating your group order…</p>
              </div>
            </template>
            <template v-else>
              <p class="go-link-label">{{ $vuetify.t('$vuetify.groupOrder.yourGroupOrderLink') }}</p>

              <div class="go-link-box">
                <v-icon small color="#aaa" style="flex-shrink:0">content_copy</v-icon>
                <span class="go-link-text">{{ groupOrderLink }}</span>
                <button class="go-copy-btn" @click="copyGroupOrderLink">
                  {{ linkCopied ? '✓ Copied!' : $vuetify.t('$vuetify.groupOrder.copy') }}
                </button>
              </div>

              <div class="go-qr-container">
                <img :src="dummyQrUrl" class="go-qr-image" alt="QR Code" />
              </div>

              <button class="go-invite-btn" @click="shareGroupOrderLink">{{ $vuetify.t('$vuetify.groupOrder.share') }}</button>
            </template>
          </div>
        </template>

      </v-card>
    </v-dialog>
    <div v-if="selectedOption === 'PICKUP'" class="pickup-details" @click.stop="isGroupOrderGuest ? showGroupOrderLocked() : SET_PICKUP_DELIVERY_EDIT_DIALOG(!isPickUpDisabled)">
      <div v-if="!!store.address && !isMobile" class="pickup-address" :class="{ small, 'disabled': isPickUpDisabled || isGroupOrderGuest }">
        <strong>{{ $vuetify.t('$vuetify.storeLocation.pickUpAt') }}</strong>
        <span v-if="!small" class="address">{{ getStoreTruncatedAddress }}</span>
        <strong v-if="small" style="margin-left: 3px">{{ $vuetify.t('$vuetify.storeLocation.time') }}</strong>
      </div>
      <div class="pickup-time" :class="{ small, 'disabled': isPickUpDisabled || isGroupOrderGuest }">
        <strong v-if="small">{{ $vuetify.t('$vuetify.storeLocation.pickUpTime') }}</strong>
        <template>
          <strong v-if="!isMobile">{{ $vuetify.t('$vuetify.storeLocation.pickUpTime') }}</strong>
          <strong v-else-if="!small">{{ $vuetify.t('$vuetify.storeLocation.time') }}</strong>
          <strong v-else="small"></strong>
        </template>
        <div style="display:flex;">
        <span class="delivery-address" :class="{ 'asap-blocked': asapBlockedState && !hasScheduledDateTime && !storeAllowsOffHourOrders(store) }">{{ formattedScheduledDateTime }}</span>
        <v-icon v-if="!isGroupOrderGuest" small>arrow_drop_down</v-icon>
        </div>
      </div>
    </div>
    <div v-else class="pickup-details" @click.stop="isGroupOrderGuest ? showGroupOrderLocked() : SET_DELIVERY_DIALOG(!isDeliveryDisabled)">
      <span class="pickup-address" :class="{ small,'disabled': isDeliveryDisabled || isGroupOrderGuest }">
        <strong>{{ $vuetify.t('$vuetify.storeLocation.deliverTo') }}</strong>
        <span @click.stop="isGroupOrderGuest ? showGroupOrderLocked() : selectOption('DELIVERY', true)" class="delivery-address" :class="{ clickable: !isGroupOrderGuest }">{{ formattedAddress }}</span>
      </span>
      <div class="pickup-time" :class="{ small, 'disabled': isDeliveryDisabled || isGroupOrderGuest }">
        <strong v-if="!isMobile">{{ $vuetify.t('$vuetify.storeLocation.deliveryTime') }}</strong>
        <strong v-else>{{ $vuetify.t('$vuetify.storeLocation.time') }}</strong>
        <div style="display:flex;">
        <span class="delivery-address" :class="{ 'asap-blocked': asapBlockedState && !hasScheduledDateTime && !storeAllowsOffHourOrders(store) }">{{ formattedScheduledDateTime }}</span>
        <v-icon v-if="!isGroupOrderGuest" small>arrow_drop_down</v-icon>
        </div>
      </div>
    </div>
    <div v-if="isInAdvanceBookingWindow" class="extraAvailableDetails info--text">
      {{$vuetify.t('$vuetify.cart.advanceBookingWindow')}}
    </div>
    <!-- allowOffHourPickup/Delivery: the store is closed but the merchant still accepts
    scheduled orders -- "Opens at {time}" would be misleading here since the guest can act
    right now, not just once the store reopens. Point them at the picker instead. Stays
    visible even after a time is picked -- "store is closed, but you can still order" is
    still true and relevant context, not a prompt that becomes stale once acted on. -->
    <div v-else-if="asapBlockedState && storeAllowsOffHourOrders(store)" class="extraAvailableDetails offHourAllowed-text">
      {{$vuetify.t('$vuetify.cart.offHourOrderingMaybeAllowed')}}
    </div>
    <!-- DEV-10499: the chip above already shows a short status ("Closed"/"Orders have been
    suspended today"); this banner always carries the specific next-availability detail,
    for both CLOSED and PAST_CUTOFF, so the guest always knows exactly when they can order.
    DEV-10821: but once a real scheduled time is picked, the chip switches to showing that
    time instead of the blocked label (see formattedScheduledDateTime) -- this banner must
    hide right along with it, or it repeats "Closed" info the guest already resolved. -->
    <div v-else-if="asapBlockedState && !hasScheduledDateTime" class="extraAvailableDetails asap-blocked-banner">
      {{ asapNextAvailableLabel }}
    </div>

    <div v-if="selectedOption === 'PICKUP' && isPickUpDisabled && !isCart" class="errorStatus" :class="{ small }" style="margin: auto;"
      v-html="$vuetify.t('$vuetify.cart.pickupDisabledUntil', [formattedPickupDisabledUntil])">
      </div>
    <div v-if="selectedOption === 'DELIVERY' && isDeliveryDisabled && !isCart" class="errorStatus" :class="{ small }" style="margin: auto;"
      v-html="$vuetify.t('$vuetify.cart.deliveryDisabledUntil', [formattedDeliveryDisabledUntil])">
      </div>
  </div>
  <div v-else-if="(closedAndNotSupportingOffHourOrders(store) || !(getStateOrderTypes.length && sortedOrderTypes && sortedOrderTypes.length)) && !isCart && !isMobile" class="isClosed">
      <span class="closed-text">
        {{ getStore.name || 'This store' }} {{ $vuetify.t('$vuetify.cart.currentlyClosed').split('.')[0] }}
      </span>
  </div>

  <!-- Host group order type change confirmation dialog -->
  <v-dialog v-model="showHostChangeTypeDialog" max-width="360" persistent>
    <v-card style="border-radius: 20px; padding: 28px 24px 20px; text-align: center;">
      <v-icon size="48" color="primary" style="margin-bottom: 12px;">swap_horiz</v-icon>
      <div style="font-size: 18px; font-weight: 700; margin-bottom: 8px;">
        {{ pendingOrderType === 'DELIVERY'
            ? $vuetify.t('$vuetify.groupOrder.confirmChangeToDelivery')
            : $vuetify.t('$vuetify.groupOrder.confirmChangeToPickup') }}
      </div>
      <div style="font-size: 13px; color: #888; margin-bottom: 20px;">
        {{ $vuetify.t('$vuetify.groupOrder.confirmChangeTypeBody') }}
      </div>
      <v-btn color="primary" block depressed style="border-radius: 25px; margin-bottom: 10px;" @click="confirmHostChangeType">{{ $vuetify.t('$vuetify.common.confirm') }}</v-btn>
      <v-btn flat block style="border-radius: 25px;" @click="showHostChangeTypeDialog = false; pendingOrderType = null">{{ $vuetify.t('$vuetify.common.cancel') }}</v-btn>
    </v-card>
  </v-dialog>

  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { EventBus } from "@/services/event-bus"
import get from 'lodash/get';
import { getStoreHoursMassaged, getStoreOpenAndClose, convertTimeFraction, parseDeliveryAddress, getDisplayLocale } from "@/services/util.service"
import { getCurrencySymbol } from "@/services/currency.service"
import moment from 'moment-timezone';
import GroupOrderOnboardingTooltip from './GroupOrder/GroupOrderOnboardingTooltip.vue';
import GroupOrderOnboardingSheet from './GroupOrder/GroupOrderOnboardingSheet.vue';

const GROUP_ORDER_TOOLTIP_SEEN_KEY = 'go_tooltip_seen';
function lsGet(key) {
  try { return window.localStorage.getItem(key); } catch (e) { return null; }
}
function lsSet(key, value) {
  try { window.localStorage.setItem(key, value); } catch (e) { /* quota/private-mode */ }
}

export default {
  name: 'OrderTypeSelector',
  components: { GroupOrderOnboardingTooltip, GroupOrderOnboardingSheet },

  props: {
    isCart: {
      type: Boolean,
      default: false
    },
    store: {
      type: Object,
      required: true
    },
    small: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showGroupOrderDialog: false,
      hasSeenGroupOrderTooltip: false,
      showGroupOrderTooltip: false,
      showHostChangeTypeDialog: false,
      pendingOrderType: null,
      groupOrderName: '',
      groupOrderStep: 'main',
      inviteOpenedFromCart: false,
      deadlineOpenedFromCart: false,
      groupOrderCode: 'grp-' + Math.random().toString(36).substring(2, 9),
      groupOrderLimit: null,
      limitChip: null,
      deadlineDate: null,
      deadlineTime: null,
      deadlineOffsetSelected: null,
      deadlineUseSpecific: false,
      inviteLoading: false,
      linkCopied: false,
    }
  },
  mounted(){
    this.hasSeenGroupOrderTooltip = lsGet(GROUP_ORDER_TOOLTIP_SEEN_KEY) === '1';
    // if (!this.isDineIn){
    //   if (this.isStoreClosed) {
    //     const allowOffHourPickup = this.getStore.settings.orderSettings.allowOffHourPickup;
    //     const allowOffHourDelivery = this.getStore.settings.orderSettings.allowOffHourDelivery;
    //     if (allowOffHourPickup) {
    //       this.selectOption('PICKUP');
    //     } else if (allowOffHourDelivery) {
    //       this.selectOption('DELIVERY');
    //     } else {
    //       console.warn('No available order types when the store is closed.');
    //     }
    //     } else {
    //       this.selectOption('PICKUP');
    //     }
    //     if (!this.hasScheduledDateTime) {
    //       EventBus.$emit('resetTime');
    //     }
    // }
    this._onOpenGroupOrderInvite = () => {
      this.inviteOpenedFromCart = true
      this.deadlineOpenedFromCart = false
      this.groupOrderStep = 'invite'
      this.showGroupOrderDialog = true
    }
    this._onOpenGroupOrderDeadline = () => {
      this.inviteOpenedFromCart = false
      this.deadlineOpenedFromCart = true
      this.seedDeadlineFromGroup()
      if (!this.hasDeadlineOptions) {
        this.$store.dispatch('showSnack', {
          text: this.$vuetify.t('$vuetify.groupOrder.noDeadlineSlots'),
          color: 'info'
        })
        return
      }
      this.groupOrderStep = 'deadline'
      this.showGroupOrderDialog = true
    }
    EventBus.$on('openGroupOrderInvite', this._onOpenGroupOrderInvite)
    EventBus.$on('openGroupOrderDeadline', this._onOpenGroupOrderDeadline)
  },
  beforeDestroy() {
    // Scoped to this instance's own handler refs -- EventBus.$off('name') with no
    // handler arg removes EVERY listener for that event, including other mounted
    // OrderTypeSelector instances (Cart.vue keeps one alive via v-show while
    // StoreLocationDetails mounts/unmounts another), which silently broke the
    // "Invite people" button in the cart after this instance unmounted.
    EventBus.$off('openGroupOrderInvite', this._onOpenGroupOrderInvite)
    EventBus.$off('openGroupOrderDeadline', this._onOpenGroupOrderDeadline)
  },
  computed: {
    showGroupOrderDot() {
      return !this.hasSeenGroupOrderTooltip
    },
    isInGroupOrder() {
      return this.$store.getters['groupOrder/isInGroupOrder']
    },
    isGroupOrderGuest() {
      return this.$store.getters['groupOrder/isInGroupOrder'] && !this.$store.state.groupOrder.isHost
    },
    isInAdvanceBookingWindow() {
      const advanceMinutes = get(this.store, 'settings.orderSettings.advanceBookingAfterStoreOpenInMinutes', 0)
      if (!advanceMinutes || !this.testStore(this.store)) return false
      const { open } = getStoreOpenAndClose(this.store)
      if (open === undefined) return false
      const { hours, minutes } = convertTimeFraction(open)
      const openTotalMinutes = hours * 60 + minutes
      const windowStartMinutes = openTotalMinutes - advanceMinutes
      const now = new Date()
      const nowTotalMinutes = now.getHours() * 60 + now.getMinutes()
      return nowTotalMinutes >= windowStartMinutes && nowTotalMinutes < openTotalMinutes
    },
    todayHours() {
      const today = new Date().getDay();
      const hours = this.getStoreHours[today];
      return hours || null;
    },
    getStoreHours() {
      return getStoreHoursMassaged(this.store)
    },
    isPickUpDisabled(){
      return this.getStore.settings.orderSettings.onlinePickupDisabledUntil && 
             new Date(this.getStore.settings.orderSettings.onlinePickupDisabledUntil) > new Date();
    },
    isDeliveryDisabled(){
      return this.getStore.settings.orderSettings.onlineDeliveryDisabledUntil && 
             new Date(this.getStore.settings.orderSettings.onlineDeliveryDisabledUntil) > new Date();
    },
    formattedPickupDisabledUntil() {
      return this.formattedDisabledUntil('Pickup');
    },
    formattedDeliveryDisabledUntil() {
      return this.formattedDisabledUntil('Delivery');
    },
      storeCurrencySymbol() {
        return getCurrencySymbol(this.getStore && this.getStore.currency)
      },
      getStoreTruncatedAddress() {
        if (!this.getStore.address) return '';
        const fullStoreAddress = `${this.getStore.address}, ${this.getStore.city}, ${this.getStore.province}, ${this.getStore.country}`
        return this.isMobile ? 
          this.truncate(fullStoreAddress, 15) : 
          this.truncate(fullStoreAddress, 30);
      },
      sortedOrderTypes() {
        const scheduledDay = get(this.getCheckoutInfo, 'scheduledDay', '');
        const scheduledTime = get(this.getCheckoutInfo, 'scheduledTime', '');

        return [...this.getStateOrderTypes]
          .sort((a, b) => {
            if (a.value === 'PICKUP') return -1;
            if (b.value === 'PICKUP') return 1;
            if (a.value === 'DELIVERY') return -1;
            if (b.value === 'DELIVERY') return 1;
            return 0;
          })
          .filter(item => {
            if (this.isStoreClosed) {
              const allowOffHourPickup = this.getStore.settings.orderSettings.allowOffHourPickup;
              // const pickupHoursLimit = this.getStore.settings.orderSettings.scheduledOrderTimeLimitInHours
              const allowOffHourDelivery = this.getStore.settings.orderSettings.allowOffHourDelivery;
              // const deliveryHoursLimit = this.getStore.settings.orderSettings.scheduledDeliveryTimeLimitInHours

              if (item.value === 'PICKUP') {
                return allowOffHourPickup 
                // && (this.tillNextOpen('PICKUP') - pickupHoursLimit) <= 0
              }
              if (item.value === 'DELIVERY') {
                return allowOffHourDelivery 
                // && (this.tillNextOpen('DELIVERY') - deliveryHoursLimit) <= 0
              }
              return false;
            } else {
              return item.value !== 'DINE_IN';
            }
          });
    },
    isMobile() {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isSmallScreen = window.innerWidth <= 920;
      const isSmall = this.small;
      return isSmall || this.$vuetify.breakpoint.smAndDown || /mobile|android|iphone|ipad|ipod/.test(userAgent) || isSmallScreen;
    },
    selectedOption: {
      get() {
        return this.getOrderType;
      },
      set(val) {
        this.setOrderType(val);
      }
    },
    hasScheduledDateTime() {
      if (!this.getCheckoutInfo) return false;
      // In a group order, always considered set — either ASAP or a specific time
      if (this.$store.getters['groupOrder/isInGroupOrder']) return true;
      const scheduledTime = this.getCheckoutInfo.scheduledTime;
      const scheduledDay = this.getCheckoutInfo.scheduledDay;
      if (!scheduledTime || !scheduledDay) return false;
      // ASAP: scheduledTime or scheduledDay is 'now' — always considered set
      if (scheduledTime === 'now') return true;
      // Scheduled: need valid formatted values
      return !!(this.formattedScheduledDay && this.formattedScheduledTime);
    },
    // DEV-10499/DEV-10812: null when ASAP is offered normally. 'CLOSED'/'PAST_CUTOFF' when
    // getStoreAvailableOrderDays didn't splice a 'now' slot in -- driven entirely by the
    // backend's own day_slots/asapAllowed (via getValidTimeSlots), never isStoreClosed. That
    // getter re-derives open/closed from the store's raw hours table client-side, with no
    // knowledge of the buffers, prep time, or scheduling limits the backend already folded
    // into asapAllowed -- trusting it here caused a real store to show "no longer accepting
    // orders" while genuinely open and offering real scheduled slots for today. asapAllowed
    // is the backend's own verdict on whether ASAP is schedule-permitted right now, so it's
    // the correct CLOSED signal; PAST_CUTOFF is reserved for the schedule-permitted-but-no-
    // now-slot-survived case (buffer/prep time consumed the rest of today). Only group order
    // GUESTS are exempt (they never pick the schedule themselves -- the host still does, via
    // _currentScheduledTime's isHost branch, so the host must still see this).
    asapBlockedState() {
      if (this.isGroupOrderGuest) return null;
      const scheduleTakeoutDatetimeManually = get(this.getStore, 'settings.orderSettings.scheduleTakeoutDatetimeManually', false);
      // getValidTimeSlots defaults to {} before the first fetchShoppingCart resolves
      // (serverCart starts as {}, and some paths reset validTimeSlots to null) -- an
      // absent day_slots key means "we don't know yet", not "ASAP isn't offered".
      // Returning a blocked state here would flash a false "Closed"/"past cutoff" on a
      // perfectly open store for the whole cart round-trip.
      const slots = this.getValidTimeSlots || {};
      if (!slots.day_slots) return null;
      // DEV-10812: scheduleTakeoutDatetimeManually is a merchant preference ("always make
      // the guest pick a time, never auto-offer ASAP") -- store-detail.js's
      // getStoreAvailableOrderDays/getDefaultSchedule deliberately never puts a 'now' slot
      // in for this case, same shape as a genuinely closed/past-cutoff store. The backend's
      // asapAllowed is still true here (this setting is frontend-only, never read
      // server-side), so without this check a perfectly open store showed a false "Orders
      // have been suspended today" purely because the merchant opted into manual
      // scheduling -- the guest could still see real time slots in the picker, just not
      // through this chip. DINE_IN is exempt: the setting only applies to takeout ordering.
      // Only short-circuits when the store is actually OPEN (asapAllowed) -- a genuinely
      // closed store must still fall through to the real CLOSED/PAST_CUTOFF branch below,
      // or a merchant with both scheduleTakeoutDatetimeManually and allowOffHourPickup/
      // Delivery on lost the "store closed, but you can still schedule" banner entirely.
      if (scheduleTakeoutDatetimeManually && this.getOrderType !== 'DINE_IN' && slots.asapAllowed) return null;
      const days = this.getStoreAvailableOrderDays || [];
      const firstDayTimes = (days[0] && days[0].times) || [];
      const firstTime = firstDayTimes.length > 0 ? firstDayTimes[0] : null;
      const asapOffered = !!(firstTime && firstTime.id === 'now');
      if (asapOffered) return null;
      return slots.asapAllowed ? 'PAST_CUTOFF' : 'CLOSED';
    },
    // Short status shown in place of a time in the chip itself, whenever ASAP is blocked --
    // the detailed "Opens {Day} at {Time}" reason lives separately in asapNextAvailableLabel
    // (the banner below), so the chip stays glanceable instead of wrapping a full sentence.
    asapBlockedLabel() {
      if (this.asapBlockedState === 'PAST_CUTOFF') {
        return this.$vuetify.t('$vuetify.cart.asapSuspendedToday');
      }
      return this.$vuetify.t('$vuetify.common.closed');
    },
    // Detailed next-availability line for the banner under the chip -- always the specific
    // "Closed · Opens {Day} at {Time}", for both CLOSED and PAST_CUTOFF, so the guest always
    // sees exactly when they can order next regardless of which blocked state put them here.
    // day/time must both resolve to real, non-empty strings; anything else (no slots resolved
    // yet, an edge case in the available-days calc) falls back to the plain cutoff copy so
    // this can never render a literal "undefined".
    asapNextAvailableLabel() {
      const days = this.getStoreAvailableOrderDays || [];
      const firstDay = days[0];
      const firstTime = firstDay && firstDay.times && firstDay.times[0];
      const day = firstDay && typeof firstDay.label === 'string' && firstDay.label;
      const time = firstTime && typeof firstTime.label === 'string' && firstTime.label;
      if (!day || !time) {
        return this.$vuetify.t('$vuetify.cart.asapPastCutoff');
      }
      // $vuetify.t(key, ...params) is variadic, NOT (key, paramsArray) -- passing an
      // array as one argument makes {0} stringify the whole array via Array.toString()
      // ("Thu, Aug 20,2:00 PM", comma-joined with no space) and {1} read past the end
      // (undefined). Pass day/time as separate positional args instead.
      return this.$vuetify.t('$vuetify.cart.asapClosed', day, time);
    },
    formattedScheduledDay() {
      if (this.$store.getters['groupOrder/isInGroupOrder']) {
        const groupScheduledTime = this.$store.state.groupOrder && this.$store.state.groupOrder.group && this.$store.state.groupOrder.group.scheduledTime;
        const tz = (this.getStore && this.getStore.timeZone) || 'America/Toronto';
        if (groupScheduledTime && groupScheduledTime !== 'now') {
          const d = new Date(groupScheduledTime);
          return isNaN(d.getTime()) ? '' : d.toLocaleDateString(getDisplayLocale(this.$vuetify.lang.current), { month: 'short', day: 'numeric', timeZone: tz });
        }
        // ASAP — show today's date in store timezone
        return new Date().toLocaleDateString(getDisplayLocale(this.$vuetify.lang.current), { month: 'short', day: 'numeric', timeZone: tz });
      }
      if (!this.getCheckoutInfo.scheduledDay) return '';
      const raw = this.getCheckoutInfo.scheduledDay;
      if (raw === 'now') return '';
      const d = new Date(raw);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString(getDisplayLocale(this.$vuetify.lang.current), { month: 'short', day: 'numeric' });
    },
    formattedScheduledTime() {
      if (!this.getCheckoutInfo.scheduledTime) return '';
      if (this.getCheckoutInfo.scheduledTime === 'now') return this.$vuetify.t('$vuetify.groupOrder.now');
      if (this.getCheckoutInfo.scheduledTime.id === 'now') return this.$vuetify.t('$vuetify.groupOrder.now');
      // In a group order, display from the server-authoritative group.scheduledTime (true UTC)
      // using store timezone. checkoutInfo.scheduledTime is locally-encoded so don't TZ-convert it.
      if (this.$store.getters['groupOrder/isInGroupOrder']) {
        const groupScheduledTime = this.$store.state.groupOrder && this.$store.state.groupOrder.group && this.$store.state.groupOrder.group.scheduledTime;
        if (groupScheduledTime && groupScheduledTime !== 'now') {
          const tz = (this.getStore && this.getStore.timeZone) || 'America/Toronto';
          const d = new Date(groupScheduledTime);
          return isNaN(d.getTime()) ? '' : d.toLocaleTimeString(getDisplayLocale(this.$vuetify.lang.current), { hour: 'numeric', minute: 'numeric', hour12: true, timeZone: tz });
        }
        return this.$vuetify.t('$vuetify.groupOrder.now');
      }
      const d = new Date(this.getCheckoutInfo.scheduledTime);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleTimeString(getDisplayLocale(this.$vuetify.lang.current), { hour: 'numeric', minute: 'numeric', hour12: true });
    },
    formattedScheduledDateTime() {
      // DEV-10499: ASAP disabled with an inline reason -- shown right in the time chip
      // itself (not a separate banner/button) since tapping the chip already opens the
      // same scheduler a dedicated "Schedule for later" action would. Never hidden,
      // never silently replaced by a scheduled time.
      // DEV-10821: but a guest can pick a valid off-hour time via the dialog even while
      // ASAP itself stays blocked (allowOffHourDelivery/Pickup) -- that real pick must win,
      // or the chip shows "Closed" forever after a successful Update. And while nothing's
      // picked yet, allowOffHourPickup/Delivery stores must show "Click to set", not
      // "Closed" -- the banner right below already says scheduling is still possible, so
      // "Closed" on the chip directly contradicts it.
      if (this.asapBlockedState && !this.hasScheduledDateTime && !this.storeAllowsOffHourOrders(this.store)) return this.asapBlockedLabel;
      if (!this.hasScheduledDateTime) return this.$vuetify.t('$vuetify.storeLocation.clickToSet');
      const locale = getDisplayLocale(this.$vuetify.lang.current);
      const tz = (this.getStore && this.getStore.timeZone) || 'America/Toronto';

      const isNow = !this.getCheckoutInfo.scheduledTime ||
        this.getCheckoutInfo.scheduledTime === 'now' ||
        (this.getCheckoutInfo.scheduledTime && this.getCheckoutInfo.scheduledTime.id === 'now');

      if (isNow) {
        const day = this.formattedScheduledDay;
        return day
          ? `${day} ${this.$vuetify.t('$vuetify.groupOrder.now')}`
          : this.$vuetify.t('$vuetify.groupOrder.now');
      }

      if (this.$store.getters['groupOrder/isInGroupOrder']) {
        const groupScheduledTime = this.$store.state.groupOrder && this.$store.state.groupOrder.group && this.$store.state.groupOrder.group.scheduledTime;
        if (groupScheduledTime && groupScheduledTime !== 'now') {
          const d = new Date(groupScheduledTime);
          if (!isNaN(d.getTime())) {
            return d.toLocaleString(locale, { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true, timeZone: tz });
          }
        }
        return this.$vuetify.t('$vuetify.groupOrder.now');
      }

      const d = new Date(this.getCheckoutInfo.scheduledTime);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleString(locale, { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true });
    },
    formattedAddress() {
      if (!this.getDeliveryAddress) return this.$vuetify.t('$vuetify.storeLocation.clickToSet');

      const { addressLine1, city, province, country } = this.getDeliveryAddress;

      if (!addressLine1 || !city || !province || !country) return this.$vuetify.t('$vuetify.storeLocation.clickToSet');

      const fullAddress = `${addressLine1}, ${city}, ${province}, ${country}`;
      return this.isMobile ? this.truncate(fullAddress, 15) : this.truncate(fullAddress, 30);
    },

    groupOrderLink() {
      // After startHostSession, use inviteUuid from Vuex (real or mock)
      if (this.$store.getters['groupOrder/isInGroupOrder']) {
        return this.$store.getters['groupOrder/inviteLink'] || '';
      }
      const base = window.location.href.split('?')[0].replace(/\/+$/, '');
      return `${base}?grp=${this.groupOrderCode}`;
    },
    dummyQrUrl() {
      const encoded = encodeURIComponent(this.groupOrderLink);
      return `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encoded}`;
    },
    deliverToText() {
      if (this.isPickUp) {
        return (this.getStore && this.getStore.name) || this.$vuetify.t('$vuetify.groupOrder.notSet');
      }
      if (!this.getDeliveryAddress) return this.$vuetify.t('$vuetify.groupOrder.notSet');
      const { addressLine1, city, province } = this.getDeliveryAddress;
      if (!addressLine1) return this.$vuetify.t('$vuetify.groupOrder.notSet');
      return [addressLine1, city, province].filter(Boolean).join(', ');
    },

    today() {
      return new Date().toISOString().substring(0, 10);
    },

    deadlineIsNow() {
      const scheduledTime = this.getCheckoutInfo && this.getCheckoutInfo.scheduledTime;
      return !scheduledTime || scheduledTime === 'now' ||
        (scheduledTime && scheduledTime.id === 'now');
    },

    deadlineDeliveryMs() {
      if (this.deadlineIsNow) return null;
      const tz = (this.getStore && this.getStore.timeZone) || 'America/Toronto';
      // Prefer group.scheduledTime (true server UTC) when the group already exists
      const groupScheduledTime = this.$store.state.groupOrder && this.$store.state.groupOrder.group && this.$store.state.groupOrder.group.scheduledTime;
      if (groupScheduledTime && groupScheduledTime !== 'now') {
        const ms = new Date(groupScheduledTime).getTime();
          return isNaN(ms) ? null : ms;
      }
      // Pre-creation: checkoutInfo.scheduledTime is a local-time-encoded Date object.
      // Reinterpret its hours/minutes in store timezone to get correct UTC ms.
      const checkoutScheduledTime = this.getCheckoutInfo && this.getCheckoutInfo.scheduledTime;
      if (!checkoutScheduledTime || checkoutScheduledTime === 'now') return null;
      const d = new Date(checkoutScheduledTime);
      if (isNaN(d.getTime())) return null;
      const ms = moment.tz({ year: d.getFullYear(), month: d.getMonth(), date: d.getDate(), hour: d.getHours(), minute: d.getMinutes(), second: 0 }, tz).valueOf();
      return ms;
    },

    deadlineNowMs() {
      return Date.now();
    },

    deadlineDeliveryDay() {
      const tz = (this.getStore && this.getStore.timeZone) || 'America/Toronto';
      // When group exists, derive date from server UTC scheduledTime in store timezone
      const groupScheduledTime = this.$store.state.groupOrder && this.$store.state.groupOrder.group && this.$store.state.groupOrder.group.scheduledTime;
      if (groupScheduledTime && groupScheduledTime !== 'now') {
        return moment.tz(groupScheduledTime, tz).format('YYYY-MM-DD');
      }
      // Pre-creation: use checkoutInfo.scheduledDay (local-time-encoded, toISOString gives correct date)
      const scheduledDay = this.getCheckoutInfo && this.getCheckoutInfo.scheduledDay;
      return scheduledDay || null;
    },

    deadlineDateOptions() {
      const toDateStr = (v) => v ? new Date(v).toISOString().split('T')[0] : null;
      const deliveryDayStr = toDateStr(this.deadlineDeliveryDay);
      const tz = (this.getStore && this.getStore.timeZone) || 'America/Toronto';
      // Reinterpret slot's local hours/minutes in store timezone to get correct UTC ms
      const slotToStoreMs = (slotDate) => {
        const d = new Date(slotDate);
        return moment.tz({ year: d.getFullYear(), month: d.getMonth(), date: d.getDate(), hour: d.getHours(), minute: d.getMinutes(), second: 0 }, tz).valueOf();
      };
      return (this.getStoreAvailableOrderDays || [])
        .filter(d => {
          if (d.id === 'default') return false;
          const dStr = toDateStr(d.id);
          if (!this.deadlineIsNow) {
            // Only allow dates up to and including the delivery day
            if (deliveryDayStr && dStr > deliveryDayStr) return false;
          }
          // Apply 30-min cutoff on the delivery day itself
          const isDeliveryDay = !this.deadlineIsNow && this.deadlineDeliveryMs !== null && dStr === deliveryDayStr;
          const cutoff = isDeliveryDay ? this.deadlineDeliveryMs - 30 * 60 * 1000 : null;
          const hasValidTime = (d.times || []).some(t => {
            if (t.id === 'now') return false;
            const timeMs = slotToStoreMs(t.id);
            if (timeMs <= this.deadlineNowMs) return false;
            if (cutoff !== null) return timeMs <= cutoff;
            return true;
          });
          return hasValidTime;
        })
        .map(d => ({
          id: d.id,
          label: new Date(d.id).toLocaleDateString(getDisplayLocale(this.$vuetify.lang.current), { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz }),
        }));
    },

    hasDeadlineOptions() {
      return this.deadlineOffsetOptions.length > 0 || this.deadlineDateOptions.length > 0;
    },
    hasSpecificTimeOptions() {
      return this.deadlineDateOptions.length > 0;
    },

    deadlineOffsetOptions() {
      const base = this.deadlineDeliveryMs;
      if (!base) return [];
      return [
        { label: '30 min', value: 30 },
        { label: '1 hour', value: 60 },
        { label: '2 hours', value: 120 },
      ].filter(opt => base - opt.value * 60 * 1000 > this.deadlineNowMs);
    },

    canSaveDeadline() {
      if (this.deadlineUseSpecific) {
        return !!this.deadlineDate && !!this.deadlineTime;
      }
      return this.deadlineOffsetSelected !== null;
    },

    deadlineTimeOptions() {
      if (!this.deadlineDate || !this.getStoreAvailableOrderDays) return [];
      const matched = this.getStoreAvailableOrderDays.find(d => d.id === this.deadlineDate);
      if (!matched) return [];

      const tz = (this.getStore && this.getStore.timeZone) || 'America/Toronto';
      // Time slot Date objects are built with setHours() in browser local time, so their
      // UTC values are wrong when browser ≠ store timezone. Reinterpret hours/minutes in
      // store timezone to get the correct UTC ms for filtering and as the saved value.
      const slotToStoreMs = (slotDate) => {
        const d = new Date(slotDate);
        return moment.tz({ year: d.getFullYear(), month: d.getMonth(), date: d.getDate(), hour: d.getHours(), minute: d.getMinutes(), second: 0 }, tz).valueOf();
      };
      // Convert a slot Date object to YYYY-MM-DD in store timezone
      const slotToDateStr = (v) => {
        if (!v) return null;
        const d = new Date(v);
        return moment.tz({ year: d.getFullYear(), month: d.getMonth(), date: d.getDate() }, tz).format('YYYY-MM-DD');
      };

      // Cutoff: 30 min before delivery, only applies on the delivery day itself (not for ASAP)
      let cutoff = null;
      const deliveryDayStr = typeof this.deadlineDeliveryDay === 'string' && this.deadlineDeliveryDay.match(/^\d{4}-\d{2}-\d{2}$/)
        ? this.deadlineDeliveryDay
        : slotToDateStr(this.deadlineDeliveryDay);
      const selectedDayStr = slotToDateStr(this.deadlineDate);
      if (!this.deadlineIsNow && this.deadlineDeliveryMs !== null && deliveryDayStr && deliveryDayStr === selectedDayStr) {
        cutoff = this.deadlineDeliveryMs - 30 * 60 * 1000;
      }

      return (matched.times || [])
        .filter(t => {
          if (t.id === 'now') return false;
          const timeMs = slotToStoreMs(t.id);
          if (timeMs <= this.deadlineNowMs) return false;
          if (cutoff !== null) return timeMs <= cutoff;
          return true;
        })
        .map(t => {
          const correctedMs = slotToStoreMs(t.id);
          return {
            id: new Date(correctedMs).toISOString(),
            label: new Date(correctedMs).toLocaleTimeString(getDisplayLocale(this.$vuetify.lang.current), { hour: 'numeric', minute: '2-digit', timeZone: tz }),
          };
        });
    },

    deadlineDisplayText() {
      if (!this.hasDeadlineOptions) return this.$vuetify.t('$vuetify.groupOrder.orderAsap');
      if (this.deadlineOffsetSelected !== null && !this.deadlineUseSpecific) {
        const opt = this.deadlineOffsetOptions.find(o => o.value === this.deadlineOffsetSelected);
        return opt ? opt.label + ' before' : this.$vuetify.t('$vuetify.groupOrder.peopleCanOrderAnytime');
      }
      if (!this.deadlineDate) return this.$vuetify.t('$vuetify.groupOrder.peopleCanOrderAnytime');
      const dateLabel = (this.deadlineDateOptions.find(d => d.id === this.deadlineDate) || {}).label || this.deadlineDate;
      const timeLabel = this.deadlineTime
        ? `, ${(this.deadlineTimeOptions.find(t => t.id === this.deadlineTime) || {}).label || this.deadlineTime}`
        : '';
      return `${dateLabel}${timeLabel}`;
    },

    deadlineDisplaySub() {
      if (!this.hasDeadlineOptions) return this.$vuetify.t('$vuetify.groupOrder.noDeadlineSlots');
      if (this.deadlineOffsetSelected !== null && !this.deadlineUseSpecific) {
        return this.$vuetify.t('$vuetify.groupOrder.orderDeadline');
      }
      return this.deadlineDate
        ? this.$vuetify.t('$vuetify.groupOrder.orderDeadline')
        : this.$vuetify.t('$vuetify.groupOrder.noDeadlineSet');
    },

    limitChips() {
      const sym = this.storeCurrencySymbol || '$'
      return [
        { label: this.$vuetify.t('$vuetify.groupOrder.limitNoLimit'), value: null },
        { label: `${sym}10`, value: 10 },
        { label: `${sym}15`, value: 15 },
        { label: `${sym}20`, value: 20 },
        { label: this.$vuetify.t('$vuetify.groupOrder.limitOther'), value: 'other' },
      ]
    },

    paymentDisplayText() {
      return this.$vuetify.t('$vuetify.groupOrder.youPayForEveryone');
    },

    paymentDisplaySub() {
      if (this.groupOrderLimit) {
        const symbol = this.storeCurrencySymbol || '$';
        return this.$vuetify.t('$vuetify.groupOrder.spendingLimitValue', [`${symbol}${this.groupOrderLimit}`]);
      }
      return this.$vuetify.t('$vuetify.groupOrder.noSpendingLimit');
    },

    ...mapGetters([
      'getStoreAvailableOrderDays',
      'isStoreClosed',
      'isHoliday',
      'getStore',
      'getCheckoutInfo',
      'getValidTimeSlots',
      'getDeliveryAddress',
      'getDeliveryAddressResult',
      'getOrderType',
      'getStateOrderTypes',
      'isDelivery',
      'isPickUp',
      'getDeliveryProvider',
      'isDineIn',
      'tillNextOpen',
      'doesStoreHaveSetting',
      'testStore',
      'getFirstName',
      'isLoggedIn'
    ])
  },
  watch: {
    // allowOffHourPickup/allowOffHourDelivery: when the store is closed and only ONE of
    // Pickup/Delivery is off-hour-enabled, that's not a real choice for the guest to make
    // -- the other tab isn't even clickable. Leaving selectedOption on whatever the app's
    // generic default (PICKUP) was left it looking disabled/grey until the guest manually
    // clicked it, even though there was only one real option. This does NOT silently swap
    // between two genuinely available types (that's still forbidden) -- it only fires when
    // sortedOrderTypes has resolved to exactly one non-DINE_IN choice. Calls setOrderType
    // directly rather than selectOption -- selectOption's !getDeliveryAddress branch would
    // pop the delivery-address dialog open unprompted on page load; this should just make
    // the correct tab active, same as if it had loaded that way to begin with.
    sortedOrderTypes: {
      immediate: true,
      handler(types) {
        if (this.isGroupOrderGuest) return
        const selectable = (types || []).filter(t => t.value !== 'DINE_IN')
        if (selectable.length === 1 && this.selectedOption !== selectable[0].value) {
          this.setOrderType(selectable[0].value)
        }
      }
    },
    getValidTimeSlots(newSlots) {
      if (newSlots && (newSlots.day_slots || newSlots.asapAllowed)) {
        if (this.$store.getters['groupOrder/isInGroupOrder']) return
        const info = this.getCheckoutInfo
        if (!info || !info.scheduledTime || !info.scheduledDay) {
          this.$store.dispatch('setDefaultCheckoutInfoIfEmpty')
        }
      }
    },
    deadlineTimeOptions(options) {
      if (options && options.length === 1) {
        this.deadlineTime = options[0].id;
      } else if (this.deadlineTime && options && !options.find(t => t.id === this.deadlineTime)) {
        this.deadlineTime = null;
      }
    },
  },
  methods: {
    selectLimitChip(value) {
      this.limitChip = value
      if (value === 'other') {
        this.groupOrderLimit = null
        this.$nextTick(() => { if (this.$refs.limitInput) this.$refs.limitInput.focus() })
      } else {
        this.groupOrderLimit = value
      }
    },

    confirmHostChangeType() {
      this.showHostChangeTypeDialog = false;
      var newOrderType = this.pendingOrderType;
      this.pendingOrderType = null;
      if (!newOrderType) return;
      if (newOrderType === 'DELIVERY') {
        // Always open delivery address dialog fresh when switching to delivery.
        // This bypasses selectOption's newOrderType=='DELIVERY' && isPickUp branch, so
        // mirror its two effects here: clear whatever slot data is cached for the OLD
        // type (Pickup) so the Delivery dialog can't read/save a stale Pickup slot, and
        // flag the switch so DeliveryDialog.handleDialogOpen knows to refetch with
        // orderTypeOverride: 'DELIVERY' instead of trusting the not-yet-flipped state.orderType.
        this.$store.commit('CLEAR_VALID_TIME_SLOTS');
        EventBus.$emit('orderTypeSwitch', true, 'DELIVERY');
        this.SET_DELIVERY_DIALOG(true);
      } else {
        this.selectOption(newOrderType, false, true);
      }
    },

    showGroupOrderLocked() {
      this.$store.dispatch('showSnack', {
        text: this.$vuetify.t('$vuetify.groupOrder.lockedInGroupOrder'),
        color: 'primary',
        timeout: 3000,
      })
    },

    openDeadlineStep() {
      this.deadlineOpenedFromCart = false
      this.groupOrderStep = 'deadline';
      this.deadlineUseSpecific = this.deadlineIsNow;
      const opts = this.deadlineDateOptions;
      if (opts && opts.length === 1) {
        this.deadlineDate = opts[0].id;
      }
    },

    selectDeadlineOffset(minutes) {
      this.deadlineOffsetSelected = minutes;
      this.deadlineUseSpecific = false;
    },

    seedDeadlineFromGroup() {
      this.deadlineDate = null;
      this.deadlineTime = null;
      this.deadlineOffsetSelected = null;
      this.deadlineUseSpecific = false;

      const countdownEndsAt = this.$store.state.groupOrder &&
        this.$store.state.groupOrder.group &&
        this.$store.state.groupOrder.group.countdownEndsAt;
      if (!countdownEndsAt) return;

      const deadline = new Date(countdownEndsAt);
      if (isNaN(deadline.getTime())) return;

      this.deadlineUseSpecific = true;
      const matchedDay = this.deadlineDateOptions.find(day => {
        const dayDate = new Date(day.id);
        return dayDate.getFullYear() === deadline.getFullYear() &&
          dayDate.getMonth() === deadline.getMonth() &&
          dayDate.getDate() === deadline.getDate();
      });
      if (!matchedDay) return;

      this.deadlineDate = matchedDay.id;
      this.$nextTick(() => {
        const matchedTime = this.deadlineTimeOptions.find(time => {
          const timeDate = new Date(time.id);
          return timeDate.getHours() === deadline.getHours() &&
            timeDate.getMinutes() === deadline.getMinutes();
        });
        this.deadlineTime = matchedTime ? matchedTime.id : null;
      });
    },

    getDeadlineCountdownEndsAt() {
      if (this.deadlineUseSpecific) {
        return this.deadlineDate && this.deadlineTime ? this.deadlineTime : null;
      }
      if (this.deadlineOffsetSelected === null) return null;

      // Prefer group.scheduledTime (server UTC) when in a group order, otherwise use checkoutInfo
      const groupScheduledTime = this.$store.state.groupOrder && this.$store.state.groupOrder.group && this.$store.state.groupOrder.group.scheduledTime;
      const checkoutScheduledTime = this.getCheckoutInfo && this.getCheckoutInfo.scheduledTime;
      const rawScheduledTime = (groupScheduledTime && groupScheduledTime !== 'now') ? groupScheduledTime : checkoutScheduledTime;
      const isAsap = !rawScheduledTime || rawScheduledTime === 'now' || (rawScheduledTime && rawScheduledTime.id === 'now');
      const baseMs = isAsap ? Date.now() : new Date(rawScheduledTime).getTime();
      const deadlineMs = baseMs - this.deadlineOffsetSelected * 60 * 1000;
      return new Date(deadlineMs).toISOString();
    },

    updateGroupDeadline(countdownEndsAt) {
      return this.$store.dispatch('groupOrder/updateDeadline', { countdownEndsAt })
        .then(() => {
          this.showGroupOrderDialog = false;
          this.groupOrderStep = 'main';
          this.deadlineOpenedFromCart = false;
        })
        .catch(() => {
          this.$store.dispatch('showSnack', {
            text: this.$vuetify.t('$vuetify.groupOrder.errorUpdateDeadline'),
            color: 'error'
          });
        });
    },

    copyGroupOrderLink() {
      navigator.clipboard.writeText(this.groupOrderLink).then(() => {
        this.linkCopied = true;
        setTimeout(() => { this.linkCopied = false; }, 2000);
      });
    },
    shareGroupOrderLink() {
      if (navigator.share) {
        navigator.share({
          title: this.groupOrderName || 'Group Order',
          text: this.$vuetify.t('$vuetify.groupOrder.shareGroupOrder'),
          url: this.groupOrderLink,
        }).catch(() => {});
      } else {
        this.copyGroupOrderLink();
      }
    },
    openDeliveryFromGroupOrder() {
      // Intentionally no-op: host sets delivery address via the main OWA flow before starting a group order
    },
    onGroupOrderDialogInput(val) {
      if (!val) this.inviteOpenedFromCart = false
    },
    onGroupOrderBtnClick() {
      if (this.hasSeenGroupOrderTooltip) {
        this.openGroupOrderDialog();
      } else {
        this.showGroupOrderTooltip = true;
      }
    },
    markGroupOrderTooltipSeen() {
      this.hasSeenGroupOrderTooltip = true;
      lsSet(GROUP_ORDER_TOOLTIP_SEEN_KEY, '1');
    },
    onGroupOrderTooltipStart() {
      this.showGroupOrderTooltip = false;
      this.markGroupOrderTooltipSeen();
      this.openGroupOrderDialog();
    },
    onGroupOrderTooltipDismiss() {
      this.showGroupOrderTooltip = false;
      this.markGroupOrderTooltipSeen();
    },
    onShowGroupOrderTooltipAgain() {
      this.showGroupOrderTooltip = true;
    },
    async openGroupOrderDialog() {
      if (!this.isLoggedIn) {
        this.$store.dispatch('showSnack', { text: 'Please log in to start a group order.', color: 'error' });
        this.setLoginDialog(true);
        return;
      }
      if (!this.isPickUp && (!this.getDeliveryAddress || !this.getDeliveryAddress.addressLine1)) {
        this.$store.dispatch('showSnack', { text: 'Please set a delivery address before starting a group order.', color: 'error' });
        this.SET_DELIVERY_DIALOG(true);
        return;
      }
      const storedCustomer = this.$store.getters['getCustomer'];
      let name = this.getFirstName || (storedCustomer && (storedCustomer.firstName || storedCustomer.name));
      if (!name) {
        try {
          const customer = await this.$store.dispatch('fetchAccountDetails');
          name = customer && (customer.firstName || customer.name);
        } catch (e) {}
      }
      const storeName = this.getStore && this.getStore.name;
      this.groupOrderName = name ? `${name}'s Group Order` : (storeName ? `${storeName} Group Order` : 'Group Order');
      this.groupOrderStep = 'main';
      this.groupOrderCode = 'grp-' + Math.random().toString(36).substring(2, 9);
      this.groupOrderLimit = null;
      this.limitChip = null;
      this.deadlineDate = null;
      this.deadlineTime = null;
      this.deadlineOffsetSelected = null;
      this.deadlineUseSpecific = false;
      // Ensure time slots are loaded so hasDeadlineOptions is accurate when dialog opens
      if (!this.getStoreAvailableOrderDays || !this.getStoreAvailableOrderDays.length) {
        this.$store.dispatch('fetchShoppingCart', { forceTimeSlots: true }).catch(function() {});
      }
      this.showGroupOrderDialog = true;
    },

    saveDeadline() {
      if (this.deadlineUseSpecific) {
        if (!this.deadlineDate || !this.deadlineTime) return;
        // deadlineTime is already an ISO string from deadlineTimeOptions
      } else if (this.deadlineOffsetSelected !== null) {
        // Use getDeadlineCountdownEndsAt which correctly resolves the base time
        const isoStr = this.getDeadlineCountdownEndsAt();
        this.deadlineTime = isoStr;
        this.deadlineDate = isoStr ? isoStr.split('T')[0] : null;
      }
      if (this.deadlineOpenedFromCart) {
        return this.updateGroupDeadline(this.getDeadlineCountdownEndsAt());
      }
      this.groupOrderStep = 'main';
    },

    clearDeadline() {
      this.deadlineDate = null;
      this.deadlineTime = null;
      this.deadlineOffsetSelected = null;
      this.deadlineUseSpecific = false;
      if (this.deadlineOpenedFromCart) {
        return this.updateGroupDeadline(null);
      }
      this.groupOrderStep = 'main';
    },

    formatDeadlineDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr + 'T00:00:00');
      const isToday = dateStr === this.today;
      const formatted = d.toLocaleDateString(getDisplayLocale(this.$vuetify.lang.current), { weekday: 'short', month: 'short', day: 'numeric' });
      return isToday ? `Today, ${formatted}` : formatted;
    },

    formatDeadlineTime(timeStr) {
      if (!timeStr) return '';
      const [h, m] = timeStr.split(':').map(Number);
      const period = h >= 12 ? 'PM' : 'AM';
      const hour = h % 12 || 12;
      return `${hour}:${String(m).padStart(2, '0')} ${period}`;
    },

    async startInviteFlow() {
      this.groupOrderStep = 'invite';
      this.inviteLoading = true;
      await this.startHostSession();
      this.inviteLoading = false;
    },

    async startHostSession() {
      const name = this.getFirstName || 'Host';

      // Build countdownEndsAt ISO string from deadlineDate + deadlineTime (if set)
      let countdownEndsAt = null;
      if (this.deadlineDate && this.deadlineTime) {
        countdownEndsAt = this.deadlineTime; // Already an ISO string from deadlineTimeOptions
      } else if (this.deadlineDate) {
        // Date only — use end of that day
        countdownEndsAt = `${this.deadlineDate}T23:59:59`;
      }

      const storeId = (this.getStore && this.getStore.storeId) || null;
      const orderType = this.getOrderType || 'PICKUP';

      const router = this.$router;
      try {
        // Build delivery address if needed
        let deliveryAddress = null;
        if (orderType === 'DELIVERY' && (this.$store.state.cart.deliveryAddress || this.getDeliveryAddressResult)) {
          deliveryAddress = parseDeliveryAddress(
            this.$store.state.cart.deliveryAddress ? { ...this.$store.state.cart.deliveryAddress } : null,
            this.getDeliveryAddress,
            this.getDeliveryAddressResult
          );
        }

        // Build ISO8601 scheduledTime in store timezone.
        // checkoutInfo time slots are built with setHours() in browser local time,
        // so we reinterpret their hours/minutes in store timezone to get correct UTC.
        let scheduledTimeISO = null;
        const storeTimezone = (this.getStore && this.getStore.timeZone) || 'America/Toronto';
        const { scheduledTime, scheduledDay } = this.getCheckoutInfo || {};
        const realScheduledTime = scheduledTime && scheduledTime !== 'now' ? scheduledTime : null;
        if (realScheduledTime && scheduledDay && orderType !== 'DINE_IN') {
          const dayD = new Date(scheduledDay);
          const timeD = new Date(realScheduledTime);
          scheduledTimeISO = moment.tz({
            year: dayD.getFullYear(), month: dayD.getMonth(), date: dayD.getDate(),
            hour: timeD.getHours(), minute: timeD.getMinutes(), second: 0,
          }, storeTimezone).toISOString();
        }

        await this.$store.dispatch('groupOrder/createGroup', {
          storeId,
          groupName: this.groupOrderName,
          guestName: name,
          countdownEndsAt,
          maxPreDiscountAmount: (this.groupOrderLimit > 0 ? this.groupOrderLimit : null),
          orderType,
          deliveryAddress,
          scheduledTime: scheduledTimeISO,
          wsHandlers: {
            onGroupPaid: function() {
              router.push({ name: 'store' }).catch(function() {});
            },
            onRemoved: function() {
              router.push({ name: 'store' }).catch(function() {});
            },
          },
        });

        // Carry over any coupon from the regular cart into the group order
        const existingCouponCode = this.$store.getters['getCouponCode'] || (this.$store.getters['getActiveCoupon'][0] && this.$store.getters['getActiveCoupon'][0].code)
        if (existingCouponCode) {
          this.$store.dispatch('groupOrder/setCoupon', { couponCode: existingCouponCode }).catch(() => {})
        }
      } catch (e) {
        this.$store.dispatch('showSnack', { text: 'Failed to start group order. Please try again.', color: 'error' });
        this.groupOrderStep = 'main';
      }
    },
    storeAllowsOffHourOrders(store) {
      return (
        this.doesStoreHaveSetting('allowOffHourPickup', false, store) ||
        this.doesStoreHaveSetting('allowOffHourDelivery', false, store)
      );
    },
    closedAndNotSupportingOffHourOrders(store) {
      const storeClosedOrHoliday = this.isStoreClosed || this.isHoliday;
      return storeClosedOrHoliday && !this.storeAllowsOffHourOrders(store);
    },
    formattedDisabledUntil(type) {
    const disabledUntil = this.getStore.settings.orderSettings[`online${type}DisabledUntil`];
    if (!disabledUntil) return '';
    const date = new Date(disabledUntil);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  },
    methodAllowed(value) {
      if (value == "DINE_IN")
        return true

      if (this.isStoreClosed) {
        const allowOffHourPickup = this.getStore.settings.orderSettings.allowOffHourPickup
        // const pickupHoursLimit = this.getStore.settings.orderSettings.scheduledOrderTimeLimitInHours

        const allowOffHourDelivery = this.getStore.settings.orderSettings.allowOffHourDelivery
        // const deliveryHoursLimit = this.getStore.settings.orderSettings.scheduledDeliveryTimeLimitInHours

        if (value == 'PICKUP')
          return allowOffHourPickup 
        // && (this.tillNextOpen('PICKUP') - pickupHoursLimit) <= 0
        if (value == 'DELIVERY')
          return allowOffHourDelivery 
        // && (this.tillNextOpen('DELIVERY') - deliveryHoursLimit) <= 0
        return false
      }

      return true
    },
    setDeliveryProvider(newProvider) {
      if (!this.getStore) {
        console.warn('Store not loaded yet');
        return;
      }

      let obj = this.getDeliveryProvider || {};
      const storeId = this.getStore.storeId;
      
      if (!storeId) {
        console.warn('Store ID not available');
        return;
      }

      obj[storeId] = newProvider;
      this.SET_DELIVERY_PROVIDER(obj);
    },
    getOrderTypeTranslation(type) {
      const knownTypes = ['DELIVERY', 'PICKUP'];
      return knownTypes.includes(type) 
        ? this.$vuetify.t(`$vuetify.orderTypes.${type.toLowerCase()}`)
        : type;
    },
    truncate(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },
    async selectOption(newOrderType, force = false, defaultOption = false) {
      if (this.methodAllowed(newOrderType)) {
        // Host in an active group order: show confirmation dialog before changing type
        // Only when the group session already exists (not during group order setup dialog)
        if (!defaultOption && this.$store.state.groupOrder.isHost && this.$store.state.groupOrder.group && newOrderType !== this.selectedOption) {
          this.pendingOrderType = newOrderType;
          this.showHostChangeTypeDialog = true;
          return;
        }

        if (force || (newOrderType === 'DELIVERY' && !this.getDeliveryAddress)) {
          this.SET_DELIVERY_DIALOG(true);
          return;
        }
        if (newOrderType == 'PICKUP' && this.isPickUp) {
          this.$store.commit('SET_PICKUP_DELIVERY_EDIT_DIALOG', true);
          return
        }
        if (newOrderType == 'DELIVERY' && this.isDelivery) {
          this.$store.commit('SET_DELIVERY_DIALOG', true);
          return
        }
        if (newOrderType == 'PICKUP' && this.isDelivery) {
          // Pickup and Delivery have different valid time slots -- discard whatever's
          // cached for Delivery so the Pickup dialog can't render/save a stale Delivery slot.
          this.$store.commit('CLEAR_VALID_TIME_SLOTS');
          EventBus.$emit('orderTypeSwitch', true, 'PICKUP');
          this.$store.commit('SET_PICKUP_DELIVERY_EDIT_DIALOG', true);
          // Group orders: don't flip local order type until the confirm dialog's
          // "Done" button actually commits it to the backend (matches the
          // !getDeliveryAddress branch above, which already defers this the same way).
          // Non-group orders never reach this branch with isInGroupOrder true, so their
          // existing (eager) behavior is unaffected.
          if (this.$store.getters['groupOrder/isInGroupOrder']) return;
        }
        if (newOrderType == 'DELIVERY' && this.isPickUp) {
          // Same reasoning as above, mirrored for the Delivery direction.
          this.$store.commit('CLEAR_VALID_TIME_SLOTS');
          EventBus.$emit('orderTypeSwitch', true, 'DELIVERY');
          if (this.$store.getters['groupOrder/isInGroupOrder']) return;
        }

      // Reset scheduledDay and scheduledTime
      if (this.getCheckoutInfo) {
        this.getCheckoutInfo.scheduledDay = null;
        this.getCheckoutInfo.scheduledTime = null;
      }

        this.setOrderType(newOrderType)
        this.$store.dispatch('setDefaultCheckoutInfoIfEmpty')
      } else {
        this.$store.dispatch('showSnack', {
          text:  `${newOrderType} is currently unavailable`,
          color: 'error'
        });

        return;
      }
      // if (this.isStoreClosed) {
      //   const allowOffHourPickup = this.getStore.settings.orderSettings.allowOffHourPickup;
      //   const allowOffHourDelivery = this.getStore.settings.orderSettings.allowOffHourDelivery;

      //   if (newOrderType === 'PICKUP' && !allowOffHourPickup) {
      //     console.warn('PICKUP is not allowed when the store is closed.');
      //     return;
      //   }
      //   if (newOrderType === 'DELIVERY' && !allowOffHourDelivery) {
      //     console.warn('DELIVERY is not allowed when the store is closed.');
      //     return;
      //   }
      // }
    },

    ...mapActions(['setOrderType', 'setDeliveryAddress']),
    ...mapMutations([
      'SET_PICKUP_DELIVERY_EDIT_DIALOG',
      'SET_DELIVERY_DIALOG',
      'SET_CHECKOUT_INFO',
      'SET_DELIVERY_PROVIDER',
      'setLoginDialog'
    ])
  }
};
</script>

<style scoped>
.group-order-modal {
  border-radius: 20px !important;
  overflow: hidden;
}

.go-modal-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 20px 0;
  position: relative;
}

.go-modal-title {
  font-size: 18px;
  font-weight: 700;
}

.go-modal-close {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: #e0e0e0;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.go-modal-close .v-icon {
  font-size: 18px;
  color: #555;
}

.go-modal-back {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.go-modal-back .v-icon {
  font-size: 22px;
  color: #444;
}

.go-pay-subtitle {
  font-size: 13px;
  color: #888;
  margin: 0;
  line-height: 1.5;
  text-align: left;
}

.go-pay-option {
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  padding: 12px 14px;
}

.go-pay-option--selected {
  border-color: var(--v-primary-base);
}

.go-pay-option--disabled {
  opacity: 0.45;
}

.go-pay-option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.go-pay-radio {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.go-pay-radio--selected {
  border-color: var(--v-primary-base);
}

.go-pay-radio-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--v-primary-base);
}

.go-pay-radio-dot--empty {
  background: transparent;
}

.go-pay-option-title {
  font-size: 14px;
  font-weight: 600;
  color: #222;
  flex: 1;
  text-align: left;
}

.go-pay-option-title--muted {
  color: #888;
  font-weight: 600;
}

.go-pay-option-sub {
  font-size: 12px;
  color: #aaa;
  margin-top: 2px;
  text-align: left;
}

.go-pay-option-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.go-coming-soon-badge {
  display: inline-flex;
  align-items: center;
  background: var(--v-primary-base);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 3px 8px;
  border-radius: 20px;
  white-space: nowrap;
  opacity: 0.85;
}

.go-limit-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
}

.go-limit-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 16px;
  border-radius: 20px;
  border: 1.5px solid #e0e0e0;
  background: #fff;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
  white-space: nowrap;
  flex: 1;
}

.go-limit-chip--active {
  background: #111;
  border-color: #111;
  color: #fff;
}

.go-limit-input-wrap {
  display: flex;
  align-items: center;
  background: #f4f4f4;
  border-radius: 10px;
  padding: 12px 14px;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  border: none;
  margin-top: 10px;
}

.go-limit-currency {
  font-size: 16px;
  font-weight: 700;
  color: #222;
  flex-shrink: 0;
}

.go-limit-per-person {
  font-size: 16px;
  font-weight: 700;
  color: #222;
  flex-shrink: 0;
  white-space: nowrap;
}

.go-limit-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 16px;
  color: #222;
  outline: none;
  width: 100%;
  min-width: 0;
}

.go-limit-input::-webkit-outer-spin-button,
.go-limit-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.go-limit-input[type=number] {
  -moz-appearance: textfield;
}

.go-limit-input::placeholder {
  color: #aaa;
}

.go-link-label {
  font-size: 15px;
  font-weight: 700;
  color: #222;
  margin: 0;
  text-align: left;
}

.go-link-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f4f4f4;
  border: 1.5px solid #ddd;
  border-radius: 14px;
  padding: 12px 14px;
}

.go-link-text {
  flex: 1;
  font-size: 12px;
  color: #555;
  line-height: 1.5;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  word-break: break-all;
}

.go-copy-btn {
  background: var(--v-primary-base);
  border: none;
  border-radius: 20px;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity 0.2s;
}

.go-copy-btn:hover {
  opacity: 0.85;
}

.go-qr-container {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}

.go-qr-image {
  width: 100%;
  max-width: 280px;
  aspect-ratio: 1;
  border-radius: 4px;
}

.go-modal-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.go-name-input {
  width: 100%;
  background: #f2f2f2;
  border: none;
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 15px;
  font-weight: 700;
  color: #222;
  outline: none;
  box-sizing: border-box;
}

.go-info-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}


.go-info-label {
  color: #999;
  font-weight: 500;
  min-width: 72px;
}

.go-info-value {
  color: #222;
  font-weight: 500;
}

.go-invite-btn {
  width: 100%;
  background: var(--v-primary-base);
  border: none;
  border-radius: 25px;
  padding: 16px;
  font-size: 16px;
  font-weight: 700;
  color: white;
  cursor: pointer;
  margin-top: 6px;
  transition: opacity 0.2s;
}

.go-invite-btn:hover {
  opacity: 0.88;
}

.closed-text {
  color: #b91c1c;
  font-weight: bold;
}
.isClosed {
  padding-top: 18px;
  height: 20vh;
  align-items: center;
  justify-content: center;
  display: flex;
}

#pickupDelivery {
  border-radius: 10px;
  margin: auto;
}

#pickupDelivery.cart-margins {
  box-sizing: border-box;
  width: 100%;
  padding-left: 15px;
  padding-right: 15px;
  margin: 0;
}

.disabled {
  opacity: 0.6;
  color: #999 !important;
  cursor: not-allowed !important;
  pointer-events: none !important;
}

.toggle-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  width: 100%;
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
}

.toggle-switch {
  display: flex;
  background: #ebebeb;
  border-radius: 50px;
  padding: 3px;
  flex: 1;
  min-width: 0;
}

.toggle-btn {
  flex: 1;
  border: none;
  padding: 0 12px;
  border-radius: 50px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s, color 0.2s;
  background: transparent;
  color: #888;
  white-space: nowrap;
  height: 36px;
  text-align: center;
  touch-action: manipulation;
}

.toggle-btn.active {
  background: var(--v-primary-base);
  color: white;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
}

.group-order-btn-wrap {
  position: relative;
  flex: 0 0 auto;
}

.group-order-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background: #ebebeb;
  border: none;
  border-radius: 50px;
  padding: 0 14px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--v-primary-base);
  white-space: nowrap;
  height: 42px;
  flex: 0 0 auto;
  transition: background 0.2s;
  touch-action: manipulation;
}

.group-order-btn:hover,
.group-order-btn:active {
  background: #dcdcdc;
}

.group-order-btn .v-icon {
  color: var(--v-primary-base) !important;
  font-size: 18px !important;
}

@keyframes goPulseRing {
  0% { box-shadow: 0 0 0 0 var(--v-primary-base); }
  75% { box-shadow: 0 0 0 9px transparent; }
  100% { box-shadow: 0 0 0 9px transparent; }
}

.go-pulse-dot {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 10px;
  height: 10px;
  background: var(--v-primary-base);
  border-radius: 999px;
  animation: goPulseRing 1.9s ease-out infinite;
}

.go-howitworks-info {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 1.5px solid var(--v-primary-base);
  color: var(--v-primary-base);
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  touch-action: manipulation;
  flex: none;
}

.pickup-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: bold;
  padding: 6px 0;
  width: 100%;
}

.pickup-address {
  flex: 1;
  text-align: right;
  text-decoration: none;
  white-space: nowrap;
  margin-right: 10px;
}

.pickup-address strong,
.pickup-time strong {
  color: var(--v-primary-base);
}
.extraAvailableDetails {
  font-size: 20px;
  margin: 5px;
  font-weight: bold
}
.offHourAllowed-text {
  color: var(--v-primary-base);
  font-size: 14px;
  font-weight: normal;
}

/* DEV-10499: ASAP blocked -- shown directly in the pickup/delivery time chip
   (tapping it already opens the same scheduler a separate button would). */
.delivery-address.asap-blocked {
  color: #b91c1c;
  font-weight: 700;
}

/* DEV-10499: next-availability detail line under the chip -- same muted red as the
   chip's own "Closed"/"suspended" status (not Vuetify's brighter red--text), and sized
   as a supporting detail rather than a shouty full-width banner. */
.asap-blocked-banner {
  font-size: 14px;
  font-weight: 600;
  color: #b91c1c;
  margin: 4px 5px;
}

.pickup-time {
  flex: 1;
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  margin-left: 10px;
}

.delivery-address {
  margin-left: 5px;
  cursor: pointer;
  text-decoration: underline;
}

.delivery-address:hover {
  opacity: 0.8;
}

.address {
margin-left: 5px !important;
}

.pickup-address 
.pickup-time {
  display: flex;
  align-items: center;
  justify-content: end;
}

.pickup-address {
  margin-right: 10px;
}

.pickup-time {
  cursor: pointer;
  margin-left: 10px;
  text-align: left;
}
.pickup-address:not(.small) {
  text-align: right;
}

.pickup-time:not(.small) {
  text-align: left;
  display:flex;
}
.pickup-address.small { 
  text-align: left;
  margin-left: 16px;
}
.pickup-time.small {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-left: 16px;
  margin-right: 16px;
}
@media only screen and (max-width: 920px) {
  #pickupDelivery.cart-margins {
    box-sizing: border-box !important;
    width: 100% !important;
    padding-left: 15px !important;
    padding-right: 15px !important;
    margin: 0 !important;
  }
  .toggle-container {
    max-width: 100%;
    padding: 0;
  }

  #pickupDelivery {
    margin-right: 0px;
    border: 0;
  }

  .pickup-details {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 4px 0;
  }

  .pickup-address {
    width: 100%;
    display: flex !important;
    justify-content: space-between;
    align-items: center;
  }

  .pickup-time {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-left: 0;
  }

  .pickup-address strong,
  .pickup-time strong {
    margin-right: 8px;
    flex-shrink: 0;
  }
  .clickable {
    min-width: 80px;
    text-align: right;
  }
  .delivery-address {
    display: inline-block;
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: right;
  }
  .pickup-address.small { 
    margin-left: unset;
    align-items: end;
  }
  .pickup-time.small {
    justify-content: space-between;
    margin-right: unset;
    margin-left: unset;
  }
}

.go-setting-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  padding: 10px 14px;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s, transform 0.1s;
  box-sizing: border-box;
}

.go-setting-row:hover {
  background: #f5f5f5;
  border-color: #ccc;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.go-setting-row:hover .go-setting-row-arrow {
  color: #555;
  transform: translateX(2px);
}

.go-setting-row:active {
  background: #eeeeee;
  transform: translateY(0);
  box-shadow: none;
}

.go-setting-row--disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.go-setting-row--disabled:hover {
  background: white;
  border-color: #e8e8e8;
  box-shadow: none;
  transform: none;
}

.go-setting-icon {
  flex-shrink: 0;
}

.go-setting-row-arrow {
  transition: color 0.15s, transform 0.15s;
}

.go-setting-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.go-setting-title {
  font-size: 14px;
  font-weight: 600;
  color: #222;
}

.go-setting-sub {
  font-size: 12px;
  color: #999;
}

.go-deadline-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.go-deadline-chip {
  padding: 8px 16px;
  border-radius: 20px;
  border: 1.5px solid #ddd;
  background: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.go-deadline-chip--selected {
  border-color: var(--v-primary-base);
  background: var(--v-primary-base);
  color: #fff;
}

.go-no-deadline-btn {
  width: 100%;
  background: transparent;
  border: 1.5px solid #ddd;
  border-radius: 25px;
  padding: 14px;
  font-size: 15px;
  font-weight: 600;
  color: #888;
  cursor: pointer;
  transition: border-color 0.2s;
}

.go-no-deadline-btn:hover {
  border-color: #bbb;
}

</style>
